import { useState, useEffect } from 'react';
import { apiService } from '../services/apiService';
import { durationFromEndTime } from '../utils/eventHelpers';
import { showToast } from '../components/Toast';

// Owns the data-fetching and mutation logic for EventsHub.js - fetching the
// event list and the current user's RSVPs, creating events, and editing RSVPs.
function useEventsHub() {
  const [events, setEvents] = useState([]);
  const [myRsvps, setMyRsvps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingRsvpId, setEditingRsvpId] = useState(null);
  const [editRsvpData, setEditRsvpData] = useState({ status: '', message: '' });
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    event_date: '',
    end_time: '',
    location: '',
    event_category: 'other',
    event_metadata: {},
    is_private: false
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchMyRsvps();
    fetchEvents(); // Fetch all events to check if user is a host
  }, []);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const response = await apiService.get('/events');
      setEvents(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to load events');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyRsvps = async () => {
    try {
      setLoading(true);
      const response = await apiService.get('/users/me/rsvps');
      setMyRsvps(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to load your RSVPs');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // Convert the local datetime to UTC for storage
      const localDate = new Date(formData.event_date);
      // Get the timezone offset in minutes and convert to milliseconds
      const timezoneOffset = localDate.getTimezoneOffset() * 60000;
      // Adjust the date to UTC by adding the offset
      const utcDate = new Date(localDate.getTime() - timezoneOffset);

      const { end_time: endTime, ...fields } = formData;
      const eventData = {
        ...fields,
        event_date: utcDate.toISOString() // Store as UTC ISO string
      };
      // No end time keeps the server's default length.
      const durationMinutes = durationFromEndTime(formData.event_date, endTime);
      if (durationMinutes) eventData.duration_minutes = durationMinutes;

      await apiService.post('/events', eventData);
      setFormData({
        title: '',
        description: '',
        event_date: '',
        end_time: '',
        location: '',
        event_category: 'other',
        event_metadata: {},
        is_private: false
      });
      fetchEvents();
      setError(null);
      return true;
    } catch (err) {
      setError('Failed to create event');
      console.error('Error:', err);
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.type === 'checkbox' ? e.target.checked : e.target.value
    });
  };

  const handleEditRsvp = (rsvp) => {
    setEditingRsvpId(rsvp.id);
    setEditRsvpData({
      status: rsvp.status,
      message: rsvp.message || ''
    });
  };

  const handleCancelEdit = () => {
    setEditingRsvpId(null);
    setEditRsvpData({ status: '', message: '' });
  };

  const handleUpdateRsvp = async (eventId) => {
    try {
      await apiService.put(`/events/${eventId}/rsvp`, editRsvpData);
      setEditingRsvpId(null);
      setEditRsvpData({ status: '', message: '' });
      fetchMyRsvps(); // Refresh the list
    } catch (err) {
      console.error('Error updating RSVP:', err);
      showToast('Failed to update RSVP', 'error');
    }
  };

  return {
    events,
    myRsvps,
    loading,
    error,
    editingRsvpId,
    setEditingRsvpId,
    editRsvpData,
    setEditRsvpData,
    formData,
    setFormData,
    submitting,
    fetchEvents,
    fetchMyRsvps,
    handleSubmit,
    handleInputChange,
    handleEditRsvp,
    handleCancelEdit,
    handleUpdateRsvp
  };
}

export default useEventsHub;
